import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Users, Search, Plus, Trash2, Edit, Phone, Mail, MessageSquare, Calendar, Tag, Filter, LogOut, BarChart3, Clock, CheckCircle, AlertCircle } from "lucide-react";
import { Badge } from "@/components/ui/badge";

const API = process.env.REACT_APP_BACKEND_URL;

const STATI = ["Nuovo", "Contattato", "Preventivo", "Chiuso Vinto", "Chiuso Perso"];
const STATI_COLORS = {
  "Nuovo": "bg-[#4285F4]/20 text-[#4285F4] border-[#4285F4]/30",
  "Contattato": "bg-[#FBBC04]/20 text-[#FBBC04] border-[#FBBC04]/30",
  "Preventivo": "bg-[#D4AF37]/20 text-[#F5E6A3] border-[#D4AF37]/30",
  "Chiuso Vinto": "bg-[#34A853]/20 text-[#34A853] border-[#34A853]/30",
  "Chiuso Perso": "bg-[#EA4335]/20 text-[#EA4335] border-[#EA4335]/30",
};
const PACCHETTI = ["Start", "Plus", "Virtual Tour", "Rooftop", "Formazione", "Altro"];
const FONTI = ["WhatsApp", "Telefono", "Google", "Passaparola", "Social", "Sito Web"];

export default function AdminPage() {
  const [authed, setAuthed] = useState(false);
  const [user, setUser] = useState("");
  const [pass, setPass] = useState("");
  const [authError, setAuthError] = useState("");
  const [leads, setLeads] = useState([]);
  const [filtro, setFiltro] = useState("Tutti");
  const [search, setSearch] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ nome: "", telefono: "", email: "", pacchetto: "Start", stato: "Nuovo", fonte: "WhatsApp", note: "" });
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API}/api/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username: user, password: pass }),
      });
      const data = await res.json();
      if (data.token) {
        localStorage.setItem("admin_token", data.token);
        setAuthed(true);
        setAuthError("");
      } else {
        setAuthError("Credenziali errate");
      }
    } catch { setAuthError("Errore di connessione"); }
  };

  const token = () => localStorage.getItem("admin_token");
  const headers = useCallback(() => ({ "Content-Type": "application/json", "Authorization": `Bearer ${token()}` }), []);

  const fetchLeads = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/admin/leads`, { headers: headers() });
      if (res.status === 401) { setAuthed(false); return; }
      const data = await res.json();
      setLeads(data.leads || []);
    } catch {}
  }, [headers]);

  useEffect(() => {
    if (localStorage.getItem("admin_token")) setAuthed(true);
  }, []);

  useEffect(() => {
    if (authed) fetchLeads();
  }, [authed, fetchLeads]);

  const saveLead = async (e) => {
    e.preventDefault();
    const url = editId ? `${API}/api/admin/leads/${editId}` : `${API}/api/admin/leads`;
    const method = editId ? "PUT" : "POST";
    await fetch(url, { method, headers: headers(), body: JSON.stringify(form) });
    setShowForm(false);
    setEditId(null);
    setForm({ nome: "", telefono: "", email: "", pacchetto: "Start", stato: "Nuovo", fonte: "WhatsApp", note: "" });
    fetchLeads();
  };

  const deleteLead = async (id) => {
    if (!window.confirm("Eliminare questo contatto?")) return;
    await fetch(`${API}/api/admin/leads/${id}`, { method: "DELETE", headers: headers() });
    fetchLeads();
  };

  const editLead = (lead) => {
    setForm({ nome: lead.nome, telefono: lead.telefono, email: lead.email, pacchetto: lead.pacchetto, stato: lead.stato, fonte: lead.fonte, note: lead.note });
    setEditId(lead.id);
    setShowForm(true);
  };

  const logout = () => { localStorage.removeItem("admin_token"); setAuthed(false); };

  const filtered = leads.filter(l => {
    if (filtro !== "Tutti" && l.stato !== filtro) return false;
    if (search && !l.nome.toLowerCase().includes(search.toLowerCase()) && !l.telefono.includes(search) && !l.email.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const stats = {
    totali: leads.length,
    nuovi: leads.filter(l => l.stato === "Nuovo").length,
    vinti: leads.filter(l => l.stato === "Chiuso Vinto").length,
    pipeline: leads.filter(l => ["Contattato", "Preventivo"].includes(l.stato)).length,
  };

  if (!authed) return (
    <div className="min-h-screen bg-[#0a0e17] flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="relative rounded-2xl p-[2px] overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-[#4285F4] via-[#EA4335] via-[#FBBC04] to-[#34A853] opacity-50"></div>
          <div className="relative bg-[#0c1220] rounded-[14px] p-8">
            <h1 className="text-2xl font-black text-center mb-1" style={{ fontFamily: "'Poppins',sans-serif", background: 'linear-gradient(135deg, #D4AF37, #F5E6A3, #D4AF37, #B8942E)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
              CRM Admin
            </h1>
            <p className="text-gray-500 text-xs text-center mb-6">schedabusiness.com</p>
            <form onSubmit={handleLogin} className="space-y-4">
              <input
                type="text" placeholder="Username" value={user} onChange={e => setUser(e.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-[#D4AF37]/50"
                data-testid="admin-login-user"
              />
              <input
                type="password" placeholder="Password" value={pass} onChange={e => setPass(e.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-[#D4AF37]/50"
                data-testid="admin-login-pass"
              />
              {authError && <p className="text-[#EA4335] text-xs">{authError}</p>}
              <button type="submit" className="w-full py-3 rounded-xl font-bold text-sm text-black" style={{ background: 'linear-gradient(135deg, #D4AF37, #F5E6A3, #D4AF37)' }} data-testid="admin-login-btn">
                Accedi
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0a0e17] text-white">
      {/* Header */}
      <header className="bg-[#0c1220] border-b border-white/10 px-4 sm:px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Users className="w-6 h-6 text-[#D4AF37]" />
            <h1 className="text-xl font-black" style={{ fontFamily: "'Poppins',sans-serif", background: 'linear-gradient(135deg, #D4AF37, #F5E6A3, #D4AF37, #B8942E)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
              CRM
            </h1>
            <span className="text-gray-500 text-sm">schedabusiness.com</span>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => { setEditId(null); setForm({ nome: "", telefono: "", email: "", pacchetto: "Start", stato: "Nuovo", fonte: "WhatsApp", note: "" }); setShowForm(true); }}
              className="flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-bold text-black" style={{ background: 'linear-gradient(135deg, #D4AF37, #F5E6A3, #D4AF37)' }} data-testid="add-lead-btn">
              <Plus className="w-4 h-4" /> Nuovo Lead
            </button>
            <button onClick={logout} className="text-gray-400 hover:text-white p-2" data-testid="admin-logout-btn"><LogOut className="w-5 h-5" /></button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          {[
            { label: "Totali", value: stats.totali, icon: Users, color: "#4285F4" },
            { label: "Nuovi", value: stats.nuovi, icon: AlertCircle, color: "#FBBC04" },
            { label: "Pipeline", value: stats.pipeline, icon: Clock, color: "#D4AF37" },
            { label: "Chiusi Vinti", value: stats.vinti, icon: CheckCircle, color: "#34A853" },
          ].map((s, i) => (
            <div key={i} className="bg-white/[0.03] border border-white/[0.08] rounded-xl p-4" data-testid={`stat-${s.label.toLowerCase()}`}>
              <div className="flex items-center gap-2 mb-1">
                <s.icon className="w-4 h-4" style={{ color: s.color }} />
                <span className="text-gray-400 text-xs">{s.label}</span>
              </div>
              <span className="text-2xl font-black" style={{ color: s.color }}>{s.value}</span>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <input type="text" placeholder="Cerca nome, telefono, email..." value={search} onChange={e => setSearch(e.target.value)}
              className="w-full bg-white/5 border border-white/10 rounded-xl pl-10 pr-4 py-2.5 text-white text-sm focus:outline-none focus:border-[#D4AF37]/50"
              data-testid="search-leads"
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            {["Tutti", ...STATI].map(s => (
              <button key={s} onClick={() => setFiltro(s)}
                className={`px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${filtro === s ? 'bg-[#D4AF37] text-black' : 'bg-white/5 text-gray-400 hover:text-white'}`}
                data-testid={`filter-${s.toLowerCase().replace(' ', '-')}`}>
                {s}
              </button>
            ))}
          </div>
        </div>

        {/* Leads Table */}
        <div className="bg-white/[0.02] border border-white/[0.08] rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="leads-table">
              <thead>
                <tr className="border-b border-white/10 text-left">
                  <th className="px-4 py-3 text-gray-400 font-bold text-xs">Nome</th>
                  <th className="px-4 py-3 text-gray-400 font-bold text-xs hidden sm:table-cell">Telefono</th>
                  <th className="px-4 py-3 text-gray-400 font-bold text-xs hidden md:table-cell">Email</th>
                  <th className="px-4 py-3 text-gray-400 font-bold text-xs">Pacchetto</th>
                  <th className="px-4 py-3 text-gray-400 font-bold text-xs">Stato</th>
                  <th className="px-4 py-3 text-gray-400 font-bold text-xs hidden lg:table-cell">Fonte</th>
                  <th className="px-4 py-3 text-gray-400 font-bold text-xs hidden lg:table-cell">Data</th>
                  <th className="px-4 py-3 text-gray-400 font-bold text-xs">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr><td colSpan="8" className="px-4 py-12 text-center text-gray-500">Nessun lead trovato</td></tr>
                ) : filtered.map(lead => (
                  <tr key={lead.id} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors" data-testid={`lead-row-${lead.id}`}>
                    <td className="px-4 py-3 font-bold text-white">{lead.nome}</td>
                    <td className="px-4 py-3 text-gray-300 hidden sm:table-cell">{lead.telefono}</td>
                    <td className="px-4 py-3 text-gray-300 hidden md:table-cell">{lead.email}</td>
                    <td className="px-4 py-3"><Badge className="bg-[#D4AF37]/15 text-[#F5E6A3] border-[#D4AF37]/30 text-[10px]">{lead.pacchetto}</Badge></td>
                    <td className="px-4 py-3"><Badge className={`${STATI_COLORS[lead.stato] || ''} text-[10px]`}>{lead.stato}</Badge></td>
                    <td className="px-4 py-3 text-gray-400 text-xs hidden lg:table-cell">{lead.fonte}</td>
                    <td className="px-4 py-3 text-gray-500 text-xs hidden lg:table-cell">{lead.created_at ? new Date(lead.created_at).toLocaleDateString('it-IT') : '-'}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <button onClick={() => editLead(lead)} className="p-1.5 rounded-lg hover:bg-white/10 text-gray-400 hover:text-[#D4AF37] transition-colors" data-testid={`edit-${lead.id}`}><Edit className="w-4 h-4" /></button>
                        {lead.telefono && <a href={`https://wa.me/${lead.telefono.replace(/\D/g,'')}`} target="_blank" rel="noopener noreferrer" className="p-1.5 rounded-lg hover:bg-white/10 text-gray-400 hover:text-[#25D366] transition-colors"><MessageSquare className="w-4 h-4" /></a>}
                        <button onClick={() => deleteLead(lead.id)} className="p-1.5 rounded-lg hover:bg-white/10 text-gray-400 hover:text-[#EA4335] transition-colors" data-testid={`delete-${lead.id}`}><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Modal Form */}
      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center px-4" onClick={() => setShowForm(false)}>
          <div className="bg-[#0c1220] border border-white/10 rounded-2xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
            <h2 className="text-lg font-black text-white mb-4" style={{ fontFamily: "'Poppins',sans-serif" }}>
              {editId ? "Modifica Lead" : "Nuovo Lead"}
            </h2>
            <form onSubmit={saveLead} className="space-y-3">
              <input type="text" placeholder="Nome *" required value={form.nome} onChange={e => setForm({...form, nome: e.target.value})}
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-[#D4AF37]/50" data-testid="form-nome" />
              <input type="text" placeholder="Telefono" value={form.telefono} onChange={e => setForm({...form, telefono: e.target.value})}
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-[#D4AF37]/50" data-testid="form-telefono" />
              <input type="email" placeholder="Email" value={form.email} onChange={e => setForm({...form, email: e.target.value})}
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-[#D4AF37]/50" data-testid="form-email" />
              <div className="grid grid-cols-3 gap-3">
                <select value={form.pacchetto} onChange={e => setForm({...form, pacchetto: e.target.value})}
                  className="bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none" data-testid="form-pacchetto">
                  {PACCHETTI.map(p => <option key={p} value={p} className="bg-[#0c1220]">{p}</option>)}
                </select>
                <select value={form.stato} onChange={e => setForm({...form, stato: e.target.value})}
                  className="bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none" data-testid="form-stato">
                  {STATI.map(s => <option key={s} value={s} className="bg-[#0c1220]">{s}</option>)}
                </select>
                <select value={form.fonte} onChange={e => setForm({...form, fonte: e.target.value})}
                  className="bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none" data-testid="form-fonte">
                  {FONTI.map(f => <option key={f} value={f} className="bg-[#0c1220]">{f}</option>)}
                </select>
              </div>
              <textarea placeholder="Note" rows={3} value={form.note} onChange={e => setForm({...form, note: e.target.value})}
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-[#D4AF37]/50 resize-none" data-testid="form-note" />
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowForm(false)} className="flex-1 py-2.5 rounded-xl bg-white/5 text-gray-400 font-bold text-sm hover:bg-white/10 transition-colors">Annulla</button>
                <button type="submit" className="flex-1 py-2.5 rounded-xl font-bold text-sm text-black" style={{ background: 'linear-gradient(135deg, #D4AF37, #F5E6A3, #D4AF37)' }} data-testid="form-save-btn">
                  {editId ? "Salva" : "Aggiungi"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
